import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkle, ArrowBendUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";

const OPTIONS = [
  { key: "not_opened", label: "Didn't open", desc: "No opens or clicks recorded — they most likely never noticed it. Best choice for a straight resend.", recommended: true },
  { key: "opened_no_click", label: "Opened but didn't click", desc: "They read it but didn't act. Best paired with tweaked content or a clearer call to action." },
  { key: "not_clicked", label: "Didn't click", desc: "Everyone without a click — includes people who read it and chose not to. Use sparingly." },
];

export const FollowUpDialog = ({ campaign, onClose }) => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [criteria, setCriteria] = useState("not_opened");
  const [subject, setSubject] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.followupCounts(campaign.id).then(setCounts).catch(() => setCounts({}));
  }, [campaign.id]);

  const sameAsOriginal = subject.trim().toLowerCase() === (campaign.subject || "").trim().toLowerCase();
  const canCreate = subject.trim() && !sameAsOriginal && !busy;

  const suggest = async () => {
    const bodyText = campaign.editor_mode === "blocks"
      ? (campaign.blocks || []).map(b => `${b.headline || ""} ${b.subtext || ""} ${b.text || ""} ${(b.html || "").replace(/<[^>]+>/g, " ")}`).join(" ")
      : (campaign.html || "").replace(/<[^>]+>/g, " ");
    setAiBusy(true);
    try {
      const r = await api.aiSubject(bodyText, "professional", 3, { mode: "followup", original_subject: campaign.subject || "" });
      setSuggestions((r.suggestions || []).filter(s => typeof s === "string" && s.trim()));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setAiBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      const doc = await api.createFollowup(campaign.id, { criteria, subject: subject.trim() });
      toast.success("Follow-up draft created — review it, then approve and send");
      onClose();
      navigate(`/campaigns/${doc.id}`);
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/40 flex items-center justify-center p-4" data-testid="followup-dialog">
      <div className="bg-white rounded-xl border border-zinc-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <ArrowBendUpLeft size={17} className="text-zinc-700" />
            <h3 className="font-heading text-base font-medium">Send follow-up</h3>
          </div>
          <button data-testid="followup-close" onClick={onClose} className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-600">Re-send <strong>{campaign.name}</strong> to recipients who didn't engage. The audience is re-checked at send time, so anyone who engages in the meantime is dropped automatically.</p>

          <div className="space-y-2">
            {OPTIONS.map(o => (
              <label key={o.key} data-testid={`followup-opt-${o.key}`} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${criteria === o.key ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                <input type="radio" name="fu-criteria" checked={criteria === o.key} onChange={() => setCriteria(o.key)} className="mt-0.5" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                    {o.label}
                    {o.recommended && <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-px">recommended</span>}
                    <span data-testid={`followup-count-${o.key}`} className="ml-auto text-xs font-semibold text-zinc-700 bg-zinc-100 rounded-full px-2 py-0.5">{counts ? (counts[o.key] ?? 0).toLocaleString() : "…"}</span>
                  </span>
                  <span className="block text-[11px] text-zinc-500 mt-0.5">{o.desc}</span>
                </span>
              </label>
            ))}
          </div>

          <p className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-md px-3 py-2">
            Heads-up: opens under-count when images are blocked, so "didn't open" includes some people who actually read the email. Bounced, unsubscribed and suppressed contacts are always excluded.
          </p>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">New subject line (required)</span>
              <button data-testid="followup-suggest" onClick={suggest} disabled={aiBusy} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40"><Sparkle size={11} /> {aiBusy ? "Thinking…" : "Suggest"}</button>
            </div>
            <p className="text-[11px] text-zinc-500 mb-1.5">Original: <span className="italic">"{campaign.subject}"</span> — the follow-up must use a different one.</p>
            <input data-testid="followup-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="A fresh angle on the same message…" className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950" />
            {sameAsOriginal && subject.trim() && <p data-testid="followup-subject-error" className="text-xs text-rose-600 mt-1">That's the same subject as the original — change it to give the email a second chance.</p>}
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {suggestions.map((s, i) => (
                  <button key={i} data-testid={`followup-suggestion-${i}`} onClick={() => setSubject(s)} className="text-xs px-2.5 py-1 rounded-full border border-zinc-200 hover:border-zinc-950 hover:bg-zinc-50 text-left">{s}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-100">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-zinc-200 hover:bg-zinc-50">Cancel</button>
          <button data-testid="followup-create" onClick={create} disabled={!canCreate} className="text-sm px-4 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40">
            {busy ? "Creating…" : "Create follow-up draft"}
          </button>
        </div>
      </div>
    </div>
  );
};
